import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/mui/Modal';
import Typography from 'components/mui/Typography';
import MButton from 'components/mui/Button';
import { Box, Alert } from '@mui/material';
import { openStore, getStoreUrl } from 'utils/storeLinks.utils';
import { openInAppBrowser } from 'utils/browser.utils';
import { isNative } from 'utils/platform.utils';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

/**
 * Update Modal Component
 * Displays force or partial update notifications
 */
const UpdateModalComponent = ({
  open,
  updateType, // 'force' or 'partial'
  updateInfo,
  onClose,
  onUpdateLater,
  platform = 'web'
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const modalStyle = {
    width: 360,
    maxWidth: '90vw',
    bgcolor: '#1A1D29',
    borderRadius: '24px',
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const titleStyle = {
    color: '#FFFFFF',
    fontSize: '24px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '8px',
    lineHeight: '1.2',
  };

  const descriptionStyle = {
    color: '#B0B0B0',
    fontSize: '15px',
    textAlign: 'center',
    marginBottom: '32px',
    lineHeight: '1.4',
    padding: '0 10px',
  };

  const alertStyle = {
    mb: 2,
    backgroundColor: '#2A2D39',
    borderColor: updateType === 'force' ? '#EA5455' : '#42A5F5'
  };

  const handleUpdate = async () => {
    const storeUrl = getStoreUrl(updateInfo?.updateUrl, platform);

    try {
      // Use in-app browser for native platforms
      if (storeUrl && isNative()) {
        await openInAppBrowser(storeUrl, {
          mode: 'system',
          presentationStyle: 'fullscreen'
        });
      } else if (storeUrl) {
        window.open(storeUrl, '_blank');
      } else {
        await openStore(platform);
      }

      // Don't close on force update
      if (updateType !== 'force') {
        onClose?.();
      }
    } catch (error) {
      console.error('Failed to open store:', error);
      // Fallback to external browser
      window.open(storeUrl, '_blank');
    }
  };

  const handleLater = () => {
    setIsClosing(true);
    setTimeout(() => {
      onUpdateLater?.();
      onClose?.();
      setIsClosing(false);
    }, 300);
  };

  const isForceUpdate = updateType === 'force';
  const updateButtonText = isForceUpdate ? 'Update Now' : 'Update';
  const storeName = platform === 'android' ? 'Play Store' : platform === 'ios' ? 'App Store' : 'Store';

  return (
    <Modal open={open} onClose={isForceUpdate ? undefined : onClose}>
      <Box sx={modalStyle}>
        {/* Icon */}
        <Box sx={{
          position: 'relative',
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: 'rgba(70, 168, 220, 0.1)',
        }}>
          <PhoneIphoneIcon sx={{ fontSize: 40, color: '#46A8DC' }} />
          <FileDownloadIcon sx={{
            fontSize: 16,
            color: '#46A8DC',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -10%)',
            backgroundColor: '#1A1D29',
            borderRadius: '50%'
          }} />
        </Box>

        {/* Title */}
        <Typography sx={titleStyle}>
          {isForceUpdate ? 'Update Required' : 'Update Available'}
        </Typography>

        {/* Description */}
        <Typography sx={descriptionStyle}>
          {updateInfo?.releaseNotes
            ? updateInfo.releaseNotes
            : isForceUpdate
              ? 'This version of the app is no longer supported. Please update to continue using the app.'
              : `A new version (${updateInfo?.latestVersion}) is available. Update now to enjoy the latest features.`
          }
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
          {!isForceUpdate && (
            <MButton
              variant="outlined"
              onClick={handleLater}
              sx={{
                flex: 1,
                color: '#FFFFFF',
                borderColor: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '600',
                '&:hover': {
                  borderColor: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Cancel
            </MButton>
          )}
          <MButton
            variant="contained"
            onClick={handleUpdate}
            sx={{
              flex: 1,
              backgroundColor: '#46A8DC',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '600',
              '&:hover': {
                backgroundColor: '#3A9ECC'
              }
            }}
          >
            Update
          </MButton>
        </Box>
      </Box>
    </Modal>
  );
};

UpdateModalComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  updateType: PropTypes.oneOf(['force', 'partial']).isRequired,
  updateInfo: PropTypes.shape({
    latestVersion: PropTypes.string,
    currentVersion: PropTypes.string,
    releaseNotes: PropTypes.string,
    updateUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]) // string = direct store_link from API
  }),
  onClose: PropTypes.func,
  onUpdateLater: PropTypes.func,
  platform: PropTypes.string
};

export default UpdateModalComponent;
